import NavigationBar from "@workspace/jobportal/src/components/NavigationBar/NavigationBar";
import { Check, Eye, EyeOff } from "lucide-react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button, FormGroup, Input, Spinner } from "reactstrap";
import { INITIAL_VALIDATION_STATE, VALIDATION_MESSAGES } from "./constants";
import "./CandidateUserSignUp.scss";
import { createCandidateUser } from "../../store/candidate-user/action";
import { encode } from "@workspace/common/src/helpers/string_helper";
import { useSelector } from "react-redux";

const CandidateUserSignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const createCandidateUserMeta = useSelector(
    (state) => state.CandidateUserReducer.createCandidateUserMeta
  );

  // Form states
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validation states
  const [validation, setValidation] = useState(INITIAL_VALIDATION_STATE);
  const [formErrors, setFormErrors] = useState({});

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (password) => {
    const newValidation = {
      passwordMatch: password === confirmPassword, // Check if passwords match
      minLength: password.length >= 8, // Check if password has at least 8 characters
      hasLower: /[a-z]/.test(password), // Check if password has at least one lowercase letter
      hasUpper: /[A-Z]/.test(password), // Check if password has at least one uppercase letter
      hasNumber: /\d/.test(password), // Check if password has at least one number
      hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password), // Check if password has special character
    };

    // Update the validation state
    setValidation(newValidation);

    return Object.values(newValidation).every(Boolean); // This will return true if all validations pass
  };

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!username?.trim()) {
      errors.username = "Username is required";
    }

    if (!firstName?.trim()) {
      errors.firstName = "First name is required";
    }

    if (!lastName?.trim()) {
      errors.lastName = "Last name is required";
    }

    if (!mobile?.trim()) {
      errors.mobile = "Mobile number is required";
    }

    if (mobile?.length < 10) {
      errors.mobile = "Mobile number must be at least 10 digits long.";
    }

    if (!email) {
      errors.email = VALIDATION_MESSAGES.REQUIRED_FIELDS;
    } else if (!validateEmail(email)) {
      errors.email = VALIDATION_MESSAGES.INVALID_EMAIL;
    }

    if (!password) {
      errors.password = VALIDATION_MESSAGES.REQUIRED_FIELDS;
    }

    if (!confirmPassword) {
      errors.confirmPassword = VALIDATION_MESSAGES.REQUIRED_FIELDS;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    validatePassword(newPassword);
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    validatePassword(password);
  };

  // Handle account creation
  const handleCreateAccount = async () => {
    if (!validateForm()) {
      return;
    }

    if (!Object.values(validation).every(Boolean)) {
      setFormErrors((prev) => ({
        ...prev,
        password: "Please ensure password meets all requirements",
      }));
      return;
    }

    try {
      const uniqueId = `candidate-${Math.random()
        .toString(36)
        .substring(2, 10)}`;
      const newUser = {
        firstName: firstName,
        lastName: lastName,
        username: username,
        email: email,
        mobile: mobile,
        location: "",
        country: "",
        designation: "",
        groups: [],
        employeeId: uniqueId,
        password: encode(password),
        confirmPassword: encode(confirmPassword),
        managerId: "",
        status: true,
        department: "",
        officeExt: "",
        officeNo: "",
      };
      // If successful, navigate to login
      dispatch(createCandidateUser({ newUser, navigate: navigate }));
    } catch (error) {
      setFormErrors((prev) => ({
        ...prev,
        general: error.message || "Failed to create account",
      }));
    }
  };

  // Reset validation when confirm password changes
  useEffect(() => {
    if (password) {
      validatePassword(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmPassword]);

  return (
    <>
      <header className="signup-navigation-bar ">
        <div>
          <NavigationBar />
        </div>
      </header>
      <div className="signup-container">
        <div style={{ width: "350px", paddingTop: "20px" }}>
          <div style={{ width: "350px" }}>
            <h3 className="fw-light text-center mb-4">Create New Account</h3>

            <div className="card-form">
              {/* General Error Message */}
              {formErrors.general && (
                <div className="alert alert-danger mb-3">
                  {formErrors.general}
                </div>
              )}

              {/* First Name Field */}
              <FormGroup className="input-containers">
                <small htmlFor="firstname">First Name*</small>
                <Input
                  className="form-control signup-form-control"
                  id="firstName"
                  type="text"
                  placeholder="Enter First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  invalid={!!formErrors.firstName}
                />
                {formErrors.firstName && (
                  <span className="text-danger small">
                    {formErrors.firstName}
                  </span>
                )}
              </FormGroup>

              {/* Last Name Field */}
              <FormGroup className="input-containers">
                <small htmlFor="lastname">Last Name*</small>
                <Input
                  className="form-control signup-form-control"
                  id="lastName"
                  type="text"
                  placeholder="Enter Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  invalid={!!formErrors.lastName}
                />
                {formErrors.lastName && (
                  <span className="text-danger small">
                    {formErrors.lastName}
                  </span>
                )}
              </FormGroup>

              {/* Username Field */}
              <FormGroup className="input-containers">
                <small htmlFor="username">Username*</small>
                <Input
                  className="form-control signup-form-control"
                  id="username"
                  type="text"
                  placeholder="Enter Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  invalid={!!formErrors.username}
                />
                {formErrors.username && (
                  <span className="text-danger small">
                    {formErrors.username}
                  </span>
                )}
              </FormGroup>

              {/* Email Field */}
              <FormGroup className="input-containers">
                <small htmlFor="email">Email*</small>
                <div className="d-flex gap-2">
                  <Input
                    className="form-control signup-form-control"
                    id="email"
                    type="email"
                    placeholder="Enter Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    invalid={!!formErrors.email}
                  />
                </div>
                {formErrors.email && (
                  <span className="text-danger small">{formErrors.email}</span>
                )}
              </FormGroup>

              {/* Mobile Field */}
              <FormGroup className="input-containers">
                <small htmlFor="email">Mobile*</small>
                <div className="d-flex gap-2">
                  <Input
                    className="form-control signup-form-control"
                    id="mobile"
                    type="text"
                    placeholder="Enter Mobile"
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value)}
                    invalid={!!formErrors.mobile}
                  />
                </div>
                {formErrors.mobile && (
                  <span className="text-danger small">{formErrors.mobile}</span>
                )}
              </FormGroup>

              {/* Password and Confirm Password Fields */}
              <FormGroup className="input-containers">
                <small htmlFor="passwords">Password*</small>
                <div className="d-flex align-items-center">
                  <Input
                    className="form-control signup-form-control"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter Password"
                    value={password}
                    onChange={handlePasswordChange}
                    invalid={!!formErrors.password}
                  />
                  <div
                    color="link"
                    className="eye"
                    style={{ marginLeft: "5px" }}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </div>
                </div>
                {formErrors.password && (
                  <span className="text-danger small">
                    {formErrors.password}
                  </span>
                )}
              </FormGroup>

              {/* Confirm Password Field */}
              <FormGroup className="input-containers">
                <small htmlFor="confirmpasswords">Confirm Password*</small>
                <div className="d-flex align-items-center">
                  <Input
                    className="form-control signup-form-control"
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    placeholder="Confirm Password"
                    onChange={handleConfirmPasswordChange}
                    invalid={!!formErrors.confirmPassword}
                  />
                  <div
                    color="link"
                    className="eye"
                    style={{ marginLeft: "5px" }}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </div>
                </div>
                {formErrors.confirmPassword && (
                  <span className="text-danger small">
                    {formErrors.confirmPassword}
                  </span>
                )}
              </FormGroup>

              {/* Password Validation Rules */}
              <div className="mb-3">
                {Object.entries(validation).map(([key, isValid]) => (
                  <div
                    key={key}
                    className="d-flex align-items-center gap-2 mb-2"
                  >
                    <div
                      className={`rounded-circle d-flex justify-content-center align-items-center p-0 ${
                        isValid ? "bg-success" : "bg-danger"
                      }`}
                      style={{ width: "18px", height: "18px" }} // Smaller size
                    >
                      <Check size={12} className="text-white" />{" "}
                      {/* Adjust icon size */}
                    </div>
                    <span className="small">
                      {key === "passwordMatch"
                        ? "Password match"
                        : key === "minLength"
                        ? "Add at least 8 characters"
                        : key === "hasLower"
                        ? "Add a lowercase letter"
                        : key === "hasUpper"
                        ? "Add an uppercase letter"
                        : key === "hasNumber"
                        ? "Add a number"
                        : key === "hasSpecial"
                        ? "Add a special character"
                        : key}
                    </span>
                  </div>
                ))}
              </div>

              {/* Create Account Button */}
              <Button
                className="signup-button"
                block
                onClick={handleCreateAccount}
                disabled={createCandidateUserMeta?.isLoading}
              >
                <span className="me-2">Create Account</span>
                {createCandidateUserMeta?.isLoading ? (
                  <Spinner size="sm">Loading...</Spinner>
                ) : (
                  <span className="mdi mdi-chevron-right mr-2"></span>
                )}
              </Button>

              <div className="text-center mt-2">
                <span className="me-1">Already have an account?</span>
                <span
                  className="cursor-pointer fw-semibold"
                  onClick={() => navigate("/candidate/login")}
                >
                  Login Here
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="signup-footer-bar">
        <p>{moment().year()} &copy; Avensys</p>
      </footer>
    </>
  );
};

export default CandidateUserSignUp;
